<template>
    <div class="accounting-statement-page-normal-display">
        <div class="doc-header">
            <div class="company-name">{{ companyName }}<span v-if="isTrying">（試用）</span></div>
            <div class="doc-name"><span v-if='isProject'>專案</span>綜合損益表</div>
            <div class="date">{{ startDateTxt }} 至 {{ endDateTxt }}</div>
            <div class="date" style="text-align: right; font-size: 8pt">製表日期：{{ todayDateTxt }}</div>
            <div v-if="settlementDateTxt" class="date no-print" style="text-align: right; font-size: 8pt">結算日期：{{ settlementDateTxt }}</div>
            <div v-if="closedDateTxt" class="date no-print" style="text-align: right; font-size: 8pt; margin-bottom: 10px">關帳日期：{{ closedDateTxt }}</div>
        </div>

        <!-- 列印用表格 -->
        <table cellspacing="0" cellpadding="0" class="print-table">
            <thead>
                <tr>
                    <th class="sign"></th>
                    <th class="name">會計項目</th>
                    <th class="amount">金額</th>
                    <th class="amount">合計金額</th>
                    <th class="percentage">佔營收 %</th>
                </tr>
            </thead>
            <tbody>
                <template v-for="item in tableData">
                    <tr v-if="item.id" :key="item.id" class="group-row">
                        <td class="sign"></td>
                        <td class="name">
                            <span>{{ item.name }}</span>
                        </td>
                        <td class="amount">
                            <!-- <money :v="item.amount" /> -->
                        </td>
                        <td class="total-amount">
                            <money v-if="item.id !== 'operatingRevenues'" :v="item.amount" />
                        </td>
                        <td class="percentage">{{ item.percentage }} %</td>
                    </tr>
                    <template v-for="subItem in item.children">
                        <tr v-if="!hideChildItems" :key="subItem.id" class="child-row">
                            <td class="sign"></td>
                            <td class="name">
                                <span v-if="subItem.isMinus"> (減項) </span>
                                <span>
                                    {{ subItem.name }}
                                    <span v-if="subItem.name === '成本支出轉資產' && !!title"> （{{ title }}） </span>
                                </span>
                            </td>
                            <td class="amount">
                                <money :v="subItem.amount" />
                            </td>
                            <td class="total-amount">
                                <!-- <money :v="item.amount" /> -->
                            </td>
                            <td class="percentage">{{ subItem.percentage }} %</td>
                        </tr>
                    </template>
                </template>
            </tbody>
        </table>

        <!-- 實際顯示表格 -->
        <ah-sticky-el-table-header>
            <el-table
                ref="table"
                :data="tableData"
                row-key="id"
                class="income-statement-table"
                :row-class-name="tableRowClassName"
                :tree-props="{ children: 'children' }"
                :default-expand-all="true"
                v-loading="isLoading"
            >
                <el-table-column prop="name" label="會計項目" fixed min-width="250">
                    <template slot-scope="scope">
                        <span
                            :class="{
                                'group-name': scope.row.level === 0,
                                'child-name': scope.row.level === 1,
                                'click-able': scope.row.level === 1,
                                'has-child': scope.row.children && scope.row.children.length > 0
                            }"
                        >
                            <span class="name-txt">
                                <span v-if="scope.row.isMinus" class="sub-label">減項</span>
                                <span @click="onClickItem(scope.row)">{{ scope.row.name }}</span>
                                <ah-tip v-if="scope.row.tip">{{ scope.row.tip }}</ah-tip>
                                <span v-if="scope.row.name === '成本支出轉資產'">
                                    （
                                    <el-input v-model="title" style="width: 150px" size="small" placeholder="填寫項目名稱" />
                                    ）
                                </span>
                            </span>
                        </span>
                    </template>
                </el-table-column>
                <el-table-column prop="amount" label="金額" align="right" width="250">
                    <template slot-scope="scope">
                        <Money v-if="scope.row.level === 1" :v="scope.row.amount" round />
                    </template>
                </el-table-column>
                <el-table-column prop="totalAmount" label="合計金額" align="right" width="250">
                    <template slot-scope="scope">
                        <Money v-if="scope.row.id !== 'operatingRevenues' && (scope.row.level === 0 || scope.row.isTotal || scope.row.isCalculate)" :v="scope.row.amount" round />
                    </template>
                </el-table-column>
                <el-table-column prop="percentage" label="佔營收%" align="right" width="150">
                    <template slot-scope="scope">
                        <span v-if="scope.row.id !== 'operatingRevenues'"> {{ percentageText(scope.row.percentage) }} % </span>
                    </template>
                </el-table-column>
            </el-table>
        </ah-sticky-el-table-header>
        <div v-if="projectList.length > 0" style="font-size: 10pt;">
            <div v-for="(project, index) in projectList" :key="project.poo">
                <div>
                    <span v-if="!index">專案：{{ project.nam }}</span>
                    <span v-else style="margin-left: 3em">{{ project.nam }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ahStickyElTableHeader from '@/components/ui/ahStickyElTableHeader.vue';

export default {
    components: {
        ahStickyElTableHeader,
    },
    props: {
        todayDateTxt: {
            type: String,
            default: '',
        },
        settlementDateTxt: {
            type: String,
            default: null,
        },
        closedDateTxt: {
            type: String,
            default: null,
        },
        startDateTxt: {
            type: String,
            default: '',
        },
        endDateTxt: {
            type: String,
            default: '',
        },
        tableData: {
            type: Array,
            default: () => [],
        },
        projectList: {
            type: Array,
            default: () => [],
        },
        isLoading: Boolean,
        hideChildItems: Boolean,
        isProject: Boolean,
    },
    computed: {
        ...mapGetters({ isTrying: 'menu/isTrying' }),
        companyName() {
            return this.$store.getters['menu/companyInfo'].cH_CMP;
        },
    },
    methods: {
        percentageText(percentage) {
            if (!percentage.includes) return '--';
            if (percentage.includes('NaN') || percentage.includes('Infinity')) {
                return '--';
            }
            return percentage;
        },
        tableRowClassName({ row }) {
            const classNames = [];

            if (row.isTotal || row.isCalculate) classNames.push('summary');
            else if (row.level === 0) classNames.push('normal');
            if (row.level === 0) classNames.push('group-row');
            // if (row.type === 1) classNames.push('add');
            // if (row.type === -1) classNames.push('minus');
            // if (row.type === 0) classNames.push('sum');
            return classNames.join(' ');
        },
        onClickItem(val) {
            this.$emit('click', val);
        },
    },
};
</script>

<style lang="scss">
.accounting-statement-page-normal-display {
    @media print {
        padding: 0 50px;
    }
    .doc-header {
        margin-top: 0px;
        text-align: center;
        padding: 10px 0 0px 0;
        line-height: 130%;

        @media print {
            margin-top: 0;
            border-top: 0;
            padding: 0;
        }

        .company-name {
            font-size: 12pt;
            @media print {
                font-size: 11pt;
            }
        }
        .doc-name {
            font-size: 13pt;
            font-weight: bold;
            margin: 3px 0;

            @media print {
                margin: 0px 0;
                font-size: 11pt;
            }
        }
        .date {
            font-size: 10pt;
            @media print {
                font-size: 9pt;
            }
        }
    }

    .income-statement-table {
        margin-bottom: 50px;
        width: 100%;
        line-height: 100%;

        @media screen {
            display: block;
        }
        @media print {
            display: none;
        }

        td {
            padding: 3px 0;
        }

        th {
            font-weight: normal;
            color: #000;
        }

        // .hover-row td {
        //     border-bottom: 1px solid #000 !important;
        //     background-color: unset;
        // }

        .group-row.el-table__row {
            td {
                font-weight: bold;
                font-size: 11.5pt;
            }
            &.normal {
                background-color: #f3f5f6;
            }
            &.normal.hover-row td {
                background-color: #ebeef0;
            }
            &.summary {
                background-color: #e1e7ee;
            }
            &.summary.hover-row td {
                background-color: #d8dfe7;
            }
        }

        .sub-label {
            color: #222;
            font-size: 9pt;
            padding: 1px 5px;
            border-radius: 4px;
            border: 1px solid #222;
            margin-right: 5px;
        }

        .group-name {
            margin: 0 0 0 37px;

            &.has-child {
                margin: 0 0 0 14px;
            }

            .name-txt {
                padding: 0 0 0 5px;
            }
        }

        .cell:has(.child-name) {
            .el-table__indent,
            .el-table__placeholder {
                display: none;
            }
            // background-color: red;
        }

        .child-name {
            display: inline-block;
            margin: 0 0 0 60px;

            .name-txt {
                // padding: 0 0 0 5px;
            }
        }
        .click-able {
            cursor: pointer;
        }
    }

    .print-table {
        width: 100%;
        line-height: 130%;
        border-top: 1px solid #000;

        @media screen {
            display: none;
        }
        @media print {
            display: table;
        }

        th {
            font-size: 9pt;
            padding: 3px 0;
        }

        td {
            padding: 0;
            font-size: 9pt;
            line-height: 130%;
            .money {
                font-weight: normal;
            }
        }

        th.sign,
        td.sign {
            width: 15pt;
            text-align: center;
        }

        th.name,
        td.name {
            text-align: left;
        }

        th.amount,
        td.amount,
        th.total-amount,
        td.total-amount,
        th.percentage,
        td.percentage {
            text-align: right;
        }

        tr.group-row td {
            border-top: 1px solid #000;
        }
        tr.child-row td.name {
            padding-left: 20pt;
            font-weight: normal;
        }
        tr.child-row td {
            border-top: 1px solid #e6e6e6;
        }
        tr.child-row td:first-child {
            border-top: 1px solid transparent;
        }
    }
}
</style>
